import { atom } from "jotai";
import type { GatingMode } from "../types/gating";
import type { AttributeCardinality } from "../components/labeling/findingAttributeValue";

export interface LabelSchema {
  type: "single_select" | "attribute_labeling" | "single_select_with_attributes";
  // single_select + single_select_with_attributes: the classification options readers choose from (e.g.
  // ["Present","Absent"] or ["Present","Absent","Cannot be assessed"]). Absent -> binary default.
  options?: string[];
  // single_select only: per-label option override, e.g. a study-level grade read alongside the
  // findings ({"PA opacification (best series)": ["Diagnostic", "Limited", ...]}). Labels not listed
  // use `options`. A label whose options exclude "Absent" is skipped by "mark rest Absent".
  options_by_label?: Record<string, string[]>;
  // Optional button abbreviation per option (default: first 3 characters).
  option_abbreviations?: Record<string, string>;
  // attribute_labeling + single_select_with_attributes: condition -> attrName -> allowed options.
  // For single_select_with_attributes the reader fills these inline when the finding call is Present
  // (ONE round); the finding row's value carries them as JSON {"call":"Present","attributes":{…}}.
  attribute_definitions?: Record<string, Record<string, string[]>>;
  // Optional per-finding cardinality. Existing worklists without this field use
  // the Head CT backward-compatible fallback in findingAttributeValue.ts.
  attribute_cardinality?: AttributeCardinality;
}

export interface ExamDisposition {
  policy: "neurovascular_reader_study";
  exclusion_group: string;
}

export interface SliceDependentConfig {
  /** Thickness in mm of the MIP slab on the sibling viewport. */
  mip_slab_thickness_mm: number;
  /** Projection mode for the slab. */
  mip_blend_mode: "MIP" | "MinIP";
  /** Currently only "current_slice" — MIP centers on the axial cursor. */
  anchor: "current_slice";
  /** If true, axial WL/WW changes propagate to the MIP viewport. */
  sync_window: boolean;
}

export interface Worklist {
  /** Worklist-owned definitions; the study endpoint validates and binds the revision. */
  study_observation_task?: import("../components/labeling/studyObservation").StudyObservationTask;
  /** Server-computed access; independent of enrollment completion state. */
  annotation_read_only?: boolean;
  external_annotation_paused?: boolean;
  project_id: string;
  name: string;
  label_schema?: LabelSchema;
  /**
   * Opts the worklist into exam-level Exclude / Technical problem (frontend #1096).
   * Worklists sharing `exclusion_group` are independent reader assignments.
   */
  exam_disposition?: ExamDisposition;
  project_type?:
    | "classification"
    | "segmentation"
    | "both"
    | "reporting"
    | "segmentation_study"
    | "joint_review";
  /** Factory cohort worklists use the study stone-review shell without study timing. */
  factory_workflow?: "standard" | "cohort_segmentation";
  source_cohort_id?: string | null;
  /**
   * Drives viewport layout selection. Backend infers from project-id prefix
   * (`hl3d-*` → `phase_dependent`, `us3d-*` → `view_dependent`,
   * `slice_dependent` is opt-in via worklist JSON, else `none`)
   * and may be overridden by an explicit value in the worklist JSON.
   * Treat absence as "none" for backwards compatibility with cached payloads.
   */
  gating_mode?: GatingMode;
  /** Only populated when `gating_mode === "slice_dependent"`. */
  slice_dependent_config?: SliceDependentConfig;
  studies: Array<{
    case_id: string;
    study_instance_uid: string;
    patient_id: string;
    accession: string;
    dicom_sources: Array<{ bucket: string; key: string }>;
    labels_to_assign: string[];
    status: string;
    template_mode?: "ai" | "blank" | "model";
    /** Cohort model output used to initialize model-arm segmentation cases. */
    model_prediction?: Record<string, unknown>;
    /** Reader-study acclimation case — untimed, excluded from capture. */
    is_practice?: boolean;
    job_id?: string;
    /** Set by canonical-only reporting worklists. Falls back as the row
     *  label when `patient_id`/`accession` are empty (canonicalize strips
     *  both during de-identification). */
    study_description?: string;
    num_series?: number | null;
    institution?: string | null;
    reader_labels?: {
      reader1: Record<string, string>;
      reader2: Record<string, string>;
    };
    disagreements?: string[];
    gt_hints?: {
      finding_count: number;
      findings: Array<{
        finding_type: string;
        laterality?: string;
        size?: string;
        renal_location?: string;
        lesion_multiplicity?: string;
        urinary_tract_location?: string;
        // Lymph-node locators (ly3d): station is the primary locator.
        nodal_station?: string;
        malignancy_suspicion?: string;
      }>;
    };
  }>;
  stats: {
    total_cases: number;
    total_labels: number;
  };
  instructions_key?: string;
  instructions_version_id?: string;
  instructions_sha256?: string;
  assigned_to?: { name: string; email: string };
}

/**
 * Compound DynamoDB row id for a worklist study. Report state (save/sign via
 * ReportingRightPanel) and post-case reader feedback (the survey in
 * LabelingPage) are persisted under the SAME `USER#/STUDY#<id>` row, so every
 * writer MUST derive the id the same way: the `::<job_id>` suffix is present
 * whenever the case has a job_id. Submitting the bare UID for one writer while
 * another uses the compound id splits the row and the backend 404s silently.
 */
export function studyRowId(
  study: Pick<Worklist["studies"][number], "study_instance_uid" | "job_id">,
): string {
  return study.job_id ? `${study.study_instance_uid}::${study.job_id}` : study.study_instance_uid;
}

// Current worklist data
export const worklistAtom = atom<Worklist | null>(null);

/** Effective gating mode for the active worklist (defaults to "none"). */
export const gatingModeAtom = atom<GatingMode>((get) => {
  const worklist = get(worklistAtom);
  return worklist?.gating_mode ?? "none";
});

/**
 * slice_dependent_config for the active worklist, or null if the worklist
 * isn't in slice_dependent mode. The backend echoes a fully-merged config
 * (defaults filled in) whenever `gating_mode === "slice_dependent"`.
 */
export const sliceDependentConfigAtom = atom<SliceDependentConfig | null>((get) => {
  const worklist = get(worklistAtom);
  if (worklist?.gating_mode !== "slice_dependent") return null;
  return worklist.slice_dependent_config ?? null;
});

// Current case index (0-57)
export const currentCaseIndexAtom = atom<number>(0);

/**
 * Label info including metadata (who labeled and when)
 */
export interface LabelInfo {
  value: string; // 'Present' | 'Absent' | 'Uncertain'
  labeled_by?: string; // User email who labeled (e.g., "michael@a2zradiology.ai")
  labeled_at?: string; // ISO timestamp when labeled
}

/**
 * Map of case_id -> label_key -> LabelInfo
 * e.g., {
 *   "GRDN0TM3_..._1.2.826...": {
 *     "Renal trauma": { value: "Present", labeled_by: "user@example.com", labeled_at: "2026-01-25T..." }
 *   }
 * }
 */
export const labelMapAtom = atom<Record<string, Record<string, LabelInfo>>>({});
